import type * as Common from '@piccolohealth/pbs-common';
import { ChakraV3, Spin } from '@piccolohealth/ui';
import React from 'react';
import { FormSection } from '../../components/forms/FormSection';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useParticipantFieldGroupTemplatesQuery } from '../../graphql/hooks/useParticipantFieldGroupTemplatesQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { ParticipantFieldTemplates } from './fields/ParticipantFieldTemplates';
import { ParticipantFieldGroupTemplates } from './groups/ParticipantFieldGroupTemplates';

export const FieldSettings = () => {
  const { organization } = useAppContext();

  const { data, isLoading, error } = useParticipantFieldGroupTemplatesQuery({
    organizationId: organization.id,
  });

  const fieldGroupTemplates = React.useMemo(
    () =>
      (data?.organization
        ?.participantFieldGroupTemplates as unknown as Common.ParticipantFieldGroupTemplate[]) ??
      [],
    [data],
  );

  const [currentFieldGroupTemplateId, setCurrentFieldGroupTemplateId] = React.useState<string>('');

  // Set the field group template to be the first in the list of group templates. If there are none
  // at all, reset the current group template to nothing.
  React.useEffect(() => {
    const [firstFieldGroupTemplate] = fieldGroupTemplates;

    if (!firstFieldGroupTemplate) {
      return setCurrentFieldGroupTemplateId('');
    }

    const startingFieldGroupTemplate = firstFieldGroupTemplate.id;

    if (!currentFieldGroupTemplateId) {
      setCurrentFieldGroupTemplateId(startingFieldGroupTemplate);
    }
  }, [currentFieldGroupTemplateId, fieldGroupTemplates]);

  if (isLoading) {
    return <Spin />;
  }

  if (error) {
    return <PiccoloError error={error} />;
  }

  return (
    <FormSection heading='Fields' description='Create groups and assign fields to them' maxW='6xl'>
      <ChakraV3.Separator />
      <ChakraV3.HStack h='xl' align='start'>
        <ChakraV3.Stack minW='xs' flexGrow={0}>
          <ParticipantFieldGroupTemplates
            fieldGroupTemplates={fieldGroupTemplates}
            currentFieldGroupTemplateId={currentFieldGroupTemplateId}
            onChangeCurrentFieldGroupTemplateId={setCurrentFieldGroupTemplateId}
          />
        </ChakraV3.Stack>
        <ChakraV3.Stack
          minH='md'
          minW='lg'
          borderLeftColor='gray.200'
          borderLeftWidth='1px'
          pl={6}
          ml={6}
        >
          <ParticipantFieldTemplates
            fieldGroupTemplateId={currentFieldGroupTemplateId}
            fieldGroupTemplates={fieldGroupTemplates}
          />
        </ChakraV3.Stack>
      </ChakraV3.HStack>
    </FormSection>
  );
};
